import { ApolloClient, ApolloLink, HttpLink, InMemoryCache } from '@apollo/client';
import { createPersistedQueryLink } from '@apollo/client/link/persisted-queries';
import { onError } from '@apollo/client/link/error';
import fetchPonyfill from 'fetch-ponyfill';
import { sha256 as jsSha256 } from 'js-sha256';
import _ from 'lodash';

import PublicEnv from 'shared/utils/public-env';
import possibleTypesIntrospection from 'shared/schemas/fragment-types';
import { ADMIN_ALWAYS_LATEST } from 'shared/constants/api-versions';
import { LD_SESSION_REPLAY_HEADER, getLDSessionReplayUrl } from 'shared/observability/ld-observability';
import { VersionLink } from './version-link';

const { fetch } = fetchPonyfill({});

const possibleTypes = {};
possibleTypesIntrospection.__schema.types.forEach((supertype) => {
  possibleTypes[supertype.name] = supertype.possibleTypes.map((type) => type.name);
});

export const EXTENSION_ERRORS = {
  AUTHERROR: ['AUTHENTICATION_ERROR', 'UNAUTHENTICATED'],
  FORBIDDEN: ['FORBIDDEN'],
  SESSIONEXPIRED: ['SESSION_INVALID'],
};

async function sha256(message) {
  if (typeof window !== 'undefined' && window.crypto?.subtle) {
    const messageBuffer = new TextEncoder().encode(message);
    const hashBuffer = await window.crypto.subtle.digest('SHA-256', messageBuffer);
    return Array.from(new Uint8Array(hashBuffer))
      .map((byte) => byte.toString(16).padStart(2, '0'))
      .join('');
  }

  return jsSha256(message);
}

const errorLinkFactory = ({ onSessionExpired, onAuthError } = {}) =>
  onError(({ graphQLErrors, networkError }) => {
    graphQLErrors?.forEach((error) => {
      if (EXTENSION_ERRORS.FORBIDDEN.includes(error.extensions?.code)) {
        onAuthError?.call(this, error);
      }

      if (EXTENSION_ERRORS.AUTHERROR.includes(error.extensions?.code)) {
        onSessionExpired?.call(this, error);
      }

      if (EXTENSION_ERRORS.SESSIONEXPIRED.includes(error.extensions?.code)) {
        onSessionExpired?.call(this, error);
      }
    });

    if (networkError) {
      console.error(`Network Error: ${networkError.message}`);
    }
  });

export const setAuthorization = (operation, forward) => {
  const headers = {};
  if (typeof window !== 'undefined') {
    const token = window.localStorage.getItem('access-token');
    headers.url = window.location.href;

    if (token) {
      headers.authorization = token;
    }

    headers['x-api-version'] = ADMIN_ALWAYS_LATEST;

    const ldSessionReplayUrl = getLDSessionReplayUrl();
    if (ldSessionReplayUrl) {
      headers[LD_SESSION_REPLAY_HEADER] = ldSessionReplayUrl;
    }
  }

  operation.setContext(() => ({ headers }));
  return forward(operation);
};

const appMapping = {
  admin: 'Admin',
};

// Apollo cache typePolicies for the main client.
//
// An id-less nested type can't be normalized, so Apollo replaces it wholesale on
// every write and a narrower selection erases fields a wider one cached.
// `merge: true` shallow-merges instead. Gotchas:
//   - Every level needs its own entry; a parent policy does not cascade.
//   - Types WITH `id` (e.g. `Dispensaries`) are normalized and accumulate fields
//     automatically — they don't need an entry.
//   - Only protects fields the incoming write omits; an explicit `null` still wins.
//   - Inert for lists of id-less objects (`categoryPhotos`,
//     `webCustomizationSettingsV2.categories.items`); those need a field-level
//     merge function on the list field, not a type policy.
export const typePolicies = {
  Dispensaries_profile_location: { merge: true },
  Dispensaries_profile_location_geometry: { merge: true },
};

const fetcher = {};
if (typeof window !== 'undefined' && typeof window.fetch === 'function') {
  fetcher.fetch = (...args) => window.fetch(...args);
} else if (typeof fetch !== 'undefined') {
  fetcher.fetch = fetch;
}

function setupMainApolloClient(appKey, { onSessionExpired, onAuthError, onUpdateVersion = _.noop } = {}) {
  const appName = _.get(appMapping, appKey, '(NO APP SET)');
  const httpLink = new HttpLink({
    credentials: 'include',
    uri: PublicEnv.graphqlUri,
    useGETForHashedQueries: true,
    ...fetcher,
  });

  return new ApolloClient({
    name: `Dutchie ${appName} Express GraphQL (${PublicEnv.appEnv})`,
    version: PublicEnv.currentVersion,
    cache: new InMemoryCache({ possibleTypes, typePolicies }),
    link: ApolloLink.from([
      VersionLink(onUpdateVersion),
      setAuthorization,
      errorLinkFactory({ onSessionExpired, onAuthError }),
      createPersistedQueryLink({ sha256, useGETForHashedQueries: true }),
      httpLink,
    ]),
  });
}

export default { setupMainApolloClient };
